import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Grid,
  Stack,
} from "@mui/material";
import { ImageOutlined, CloudUpload } from "@mui/icons-material";
import { toastAlert } from "../../../../utils/AlertContext";
import { useLogoStyles } from "../../../../hooks/panelEditor/useLogoStyles";
import PositionControls from "../globalStylesSection/components/PositionControls";
import SizeControl from "../globalStylesSection/components/SizeControl";
import { POSITIONS } from "../globalStylesSection/constants/styleDefaults";

const Logo = ({ token, idDocument }) => {
  const {
    logoStyles: serverLogoStyles,
    updateLogoStyles,
    loading: loadingLogo,
    saving: savingLogo,
    refresh: refreshLogo,
  } = useLogoStyles(token, idDocument);

  const [logoStyles, setLogoStyles] = useState({
    active: true,
    position: "left",
    image: "",
    imageFile: null,
    width: "",
    height: "",
  });

  useEffect(() => {
    if (serverLogoStyles) {
      setLogoStyles((prev) => ({
        ...prev,
        active: serverLogoStyles.active ?? true,
        position: serverLogoStyles.position || "left",
        image: serverLogoStyles.image || "",
        imageFile: null,
        width: serverLogoStyles.width || "",
        height: serverLogoStyles.height || "",
      }));
    }
  }, [serverLogoStyles]);

  const handleLogoChange = (updates) => {
    setLogoStyles((prev) => ({ ...prev, ...updates }));
  };

  const handleImageUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) =>
          handleLogoChange({
            image: ev.target.result,
            imageFile: file,
          });
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handleSaveLogo = async () => {
    try {
      const result = await updateLogoStyles({
        active: logoStyles.active,
        position: logoStyles.position,
        imageFile: logoStyles.imageFile,
        image: logoStyles.image,
        width: logoStyles.width,
        height: logoStyles.height,
      });
      if (result.success) {
        refreshLogo();
        toastAlert("Logo guardado exitosamente", "success");
      } else {
        toastAlert(result.error || "Error al guardar el logo", "error");
      }
    } catch (error) {
      toastAlert("Error al guardar el logo", "error");
    }
  };

  return (
    <Card sx={{ borderRadius: "16px", bgcolor: "#ffffff" }}>
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <ImageOutlined sx={{ fontSize: 20, color: "#1976d2" }} />
          <Typography variant="h6" fontWeight="bold">
            Logo
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Configura el logo del documento: activación, imagen y posición.
        </Typography>
        <Stack spacing={3}>
          <FormControlLabel
            control={
              <Switch
                checked={logoStyles?.active || false}
                onChange={(e) =>
                  handleLogoChange({ active: e.target.checked })
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#1976d2",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: "#1976d2",
                  },
                }}
              />
            }
            label={logoStyles?.active ? "Desactivar Logo" : "Activar Logo"}
          />

          {logoStyles?.active && (
            <>
            <Grid container alignItems="center" justifyContent="center">
                {logoStyles?.image && (
                  <Box mt={2}>
                    <img
                      src={logoStyles.image}
                      alt="Logo preview"
                      style={{ maxHeight: 60 }}
                    />
                  </Box>
                )}
              </Grid>
              <Button
                variant="contained"
                startIcon={<CloudUpload sx={{ fontSize: 20 }} />}
                onClick={handleImageUpload}
                sx={{
                  bgcolor: "#1976d2",
                  color: "white",
                  borderRadius: "50px",
                  px: 4,
                  py: 1.5,
                  textTransform: "capitalize",
                  "&:hover": { bgcolor: "#1565c0" },
                }}
              >
                {logoStyles?.image ? "Cambiar imagen" : "Añadir imagen"}
              </Button>

              

              <PositionControls
                value={logoStyles?.position}
                onChange={(value) =>
                  handleLogoChange({ position: value, active: true })
                }
                label="Posición del logo"
                positions={POSITIONS}
              />
              <SizeControl
                width={logoStyles?.width}
                height={logoStyles?.height}
                onChangeWidth={(w) => handleLogoChange({ width: w, active: true })}
                onChangeHeight={(h) => handleLogoChange({ height: h, active: true })}
                label="Dimensiones del logo (cm)"
              />
            </>
          )}
          <Grid container justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={handleSaveLogo}
                  disabled={savingLogo || loadingLogo}
                  sx={{
                    mt: 2,
                    bgcolor: "#1976d2",
                    color: "white",
                    borderRadius: "50px",
                    px: 4,
                    py: 1.5,
                    textTransform: "capitalize",
                    "&:hover": { bgcolor: "#1565c0" },
                    "&:disabled": {
                      bgcolor: "#e0e0e0",
                      color: "#9e9e9e",
                    },
                  }}
                >
                  {savingLogo ? "Guardando..." : "Guardar Cambios"}
                </Button>
              </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default Logo;